import React, { useCallback, useContext, useState } from 'react';
import { style } from 'typestyle';
import Icon from 'mdi-react/ExclamationIcon';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { useTranslation } from 'react-i18next';
import QRCodeGenerationModal from '../modals/qr-code-generation-modal.component';
import type DashboardOffice from '../models/dashboard-office';
import DashboardOfficeModal from '../modals/dashboard-office.modal.component';
import * as styles from './cards.styles';
import FloorplanUploadModal from '../modals/floorplan-upload-modal.component';
import Column from '../../../core/components/column/column.component';
import { MainAxisAlignment } from '../../../core/components/flex/flex.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';

interface Props {
  readonly dashboardOffice: DashboardOffice;
  readonly handleDidDeleteOffice: (dashboardOffice: DashboardOffice) => void;
}

const DashboardOfficeCard = ({ dashboardOffice, handleDidDeleteOffice }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const [stateOffice, setStateOffice] = useState(dashboardOffice);
  const [showDashboardOfficeModal, setShowDashboardOfficeModal] = useState(false);
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [showFloorplanModal, setShowFloorplanModal] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState<HTMLElement | undefined>(undefined);

  const handleDidUpdateOffice = useCallback(
    (updatedDashboardOffice: DashboardOffice): void => {
      setStateOffice(updatedDashboardOffice);
    },
    [setStateOffice],
  );

  const handleMenuClick = (currentTarget: React.SetStateAction<HTMLElement | undefined>): void => {
    setSelectedMenuItem(currentTarget);
  };
  const handleMenuClose = (): void => {
    setSelectedMenuItem(undefined);
  };
  const onMenuItemClick = (): void => {
    setShowQRCodeModal(true);
    handleMenuClose();
  };

  const onFloorplanItemClick = (): void => {
    setShowFloorplanModal(true);
    handleMenuClose();
  };

  const { t } = useTranslation();

  return (
    <>
      <div className={style(styles.dashboardSubCard(true))}>
        <Row>
          <div
            className={style(styles.expandedContainer)}
            key={stateOffice.id}
            onClick={(): void => {
              setShowDashboardOfficeModal(true);
            }}
            role="presentation"
          >
            {isMobile ? (
              <Column mainAxisAlignment={MainAxisAlignment.Stretch} itemSpacing={12}>
                <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
                  <Column>
                    <span className={style(styles.cardTitle)}>{t('office-name')}</span>
                    <span className={style(styles.cardBody)}>{stateOffice.name}</span>
                  </Column>
                  {stateOffice.permittedAdmins.filter((permittedAdmin) => !permittedAdmin.hasAccount).length > 0 && (
                    <Icon className={style(styles.actionIcon(false))} />
                  )}
                </Row>
                <Column>
                  <span className={style(styles.cardTitle)}>{t('number-of-users')}</span>
                  <span className={style(styles.cardBody)}>{stateOffice.permittedUserAmount}</span>
                </Column>
                <Column>
                  <span className={style(styles.cardTitle)}>{t('owner')}</span>
                  <span className={style(styles.cardBody)}>{stateOffice.authorEmail}</span>
                </Column>
              </Column>
            ) : (
              <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
                {stateOffice.permittedAdmins.some((permittedAdmin) => !permittedAdmin.hasAccount) && (
                  <Icon className={style(styles.actionIcon(false))} />
                )}
                <Column>
                  <span className={style(styles.cardTitle)}>{t('office-name')}</span>
                  <span className={style(styles.cardBody)}>{stateOffice.name}</span>
                </Column>
                <Column>
                  <span className={style(styles.cardTitle)}>{t('number-of-users')}</span>
                  <span className={style(styles.cardBody)}>{stateOffice.permittedUserAmount}</span>
                </Column>
                <Column>
                  <span className={style(styles.cardTitle)}>{t('owner')}</span>
                  <span className={style(styles.cardBody)}>{stateOffice.authorEmail}</span>
                </Column>
              </Row>
            )}
          </div>
          <Column mainAxisAlignment={MainAxisAlignment.Start}>
            <IconButton
              className={style(styles.cardTitle)}
              onClick={(event): void => {
                handleMenuClick(event.currentTarget);
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              className={style(styles.cardTitle)}
              anchorEl={selectedMenuItem}
              onClose={handleMenuClose}
              open={Boolean(selectedMenuItem)}
            >
              <MenuItem onClick={onMenuItemClick}>
                <span className={style(styles.cardBody)}>{t('qr-codes')}</span>
              </MenuItem>
              <MenuItem onClick={onFloorplanItemClick}>
                <span className={style(styles.cardBody)}>{t('floor-plan')}</span>
              </MenuItem>
            </Menu>
          </Column>
        </Row>
      </div>
      <DashboardOfficeModal
        showModal={showDashboardOfficeModal}
        dashboardOffice={stateOffice}
        handleDidDeleteOffice={handleDidDeleteOffice}
        handleDidUpdateOffice={handleDidUpdateOffice}
        handleClose={(): void => {
          setShowDashboardOfficeModal(false);
        }}
      />
      <QRCodeGenerationModal
        showModal={showQRCodeModal}
        dashboardOffice={stateOffice}
        handleClose={(): void => {
          setShowQRCodeModal(false);
        }}
      />
      <FloorplanUploadModal
        showModal={showFloorplanModal}
        dashboardOfficeId={stateOffice.id ?? ''}
        handleClose={(): void => {
          setShowFloorplanModal(false);
        }}
      />
    </>
  );
};

export default DashboardOfficeCard;
